import axios, { AxiosResponse } from "axios";
import { getValidToken } from "../../../utils/authUtils";
import {
  Project,
  ProjectModule,
  Allocation,
  AllocationTag,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateAllocationRequest,
  CreateAllocationWithTagsRequest,
  UpdateAllocationRequest,
  CreateAllocationTagRequest,
  UpdateAllocationTagRequest,
  ProjectFilter,
  AllocationFilter,
  AllocationTagFilter,
  ProjectStatus,
  ApiResponse,
  Summary,
  ProjectActivityLog,
  AllocationSummary,
  AllocationSummaryArray,
  CreateSuperProjectRequest,
  SuperProject,
} from "../types";
import { SuperProjectWithAllocationSummary } from "../pages/DivisionList";

// Base API configuration
const API_BASE_URL = `${process.env.REACT_APP_API_HOST}project-module/`;

// Helper function to handle API responses
const handleApiResponse = <T>(response: AxiosResponse<ApiResponse<T>>): T => {
  if (response.data.isSuccess) {
    return response.data.data!;
  } else {
    throw new Error(response.data.error || "API request failed");
  }
};

// Helper function to get auth headers
const getAuthHeaders = async () => {
  const token = await getValidToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

// Helper function to build query parameters
const buildQueryParams = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, v));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });
  return searchParams.toString();
};

// Project API Service
export const projectApi = {
  // Create a new project
  createProject: async (data: CreateProjectRequest): Promise<Project> => {
    try {
      const response = await axios.post<ApiResponse<Project>>(
        `${API_BASE_URL}projects`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating project:", error);
      throw error;
    }
  },

  // Get all projects with optional filters
  getProjects: async (filters?: ProjectFilter): Promise<Project[]> => {
    try {
      const queryParams = filters ? `?${buildQueryParams(filters)}` : "";
      const response = await axios.get<ApiResponse<Project[]>>(
        `${API_BASE_URL}projects${queryParams}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching projects:", error);
      throw error;
    }
  },

  getAssignedProjects: async (filters?: ProjectFilter): Promise<Project[]> => {
    try {
      const queryParams = filters ? `?${buildQueryParams(filters)}` : "";
      const response = await axios.get<ApiResponse<Project[]>>(
        `${API_BASE_URL}projects/get-assigned-projects${queryParams}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching projects:", error);
      throw error;
    }
  },

  // Get project by ID
  getProject: async (projectId: string): Promise<Project> => {
    try {
      const response = await axios.get<ApiResponse<Project>>(
        `${API_BASE_URL}projects/${projectId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching project:", error);
      throw error;
    }
  },

  getSummary: async (id: string): Promise<Summary> => {
    try {
      const response = await axios.get<ApiResponse<Summary>>(
        `${API_BASE_URL}projects/summary/${id}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching summary:", error);
      throw error;
    }
  },

  // Update project
  updateProject: async (
    projectId: string,
    data: UpdateProjectRequest
  ): Promise<Project> => {
    try {
      const response = await axios.put<ApiResponse<Project>>(
        `${API_BASE_URL}projects/${projectId}`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error updating project:", error);
      throw error;
    }
  },

  updateProjectStatus: async (
    projectId: string,
    data: UpdateProjectRequest
  ): Promise<Project> => {
    try {
      const response = await axios.put<ApiResponse<Project>>(
        `${API_BASE_URL}projects/status/${projectId}`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error updating project status:", error);
      throw error;
    }
  },

  // Delete project
  deleteProject: async (projectId: string): Promise<void> => {
    try {
      await axios.delete<ApiResponse<void>>(
        `${API_BASE_URL}projects/${projectId}`,
        { headers: await getAuthHeaders() }
      );
    } catch (error) {
      console.error("Error deleting project:", error);
      throw error;
    }
  },

  // Get projects by client
  getProjectsByClient: async (clientId: string): Promise<Project[]> => {
    try {
      const response = await axios.get<ApiResponse<Project[]>>(
        `${API_BASE_URL}projects/client/${clientId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching projects by client:", error);
      throw error;
    }
  },

  // Get projects by status
  getProjectsByStatus: async (status: ProjectStatus): Promise<Project[]> => {
    try {
      const response = await axios.get<ApiResponse<Project[]>>(
        `${API_BASE_URL}projects/status/${status}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching projects by status:", error);
      throw error;
    }
  },

  // Get active projects
  getActiveProjects: async (): Promise<Project[]> => {
    try {
      const response = await axios.get<ApiResponse<Project[]>>(
        `${API_BASE_URL}projects/active`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching active projects:", error);
      throw error;
    }
  },

  // Get completed projects
  getCompletedProjects: async (): Promise<Project[]> => {
    try {
      const response = await axios.get<ApiResponse<Project[]>>(
        `${API_BASE_URL}projects/completed`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching completed projects:", error);
      throw error;
    }
  },

  // Get projects by date range
  getProjectsByDateRange: async (
    startDate: string,
    endDate: string
  ): Promise<Project[]> => {
    try {
      const response = await axios.get<ApiResponse<Project[]>>(
        `${API_BASE_URL}projects/date-range?startDate=${startDate}&endDate=${endDate}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching projects by date range:", error);
      throw error;
    }
  },
};

// Allocation API Service
export const allocationApi = {
  // Create a new allocation
  createAllocation: async (
    data: CreateAllocationRequest
  ): Promise<Allocation> => {
    try {
      const response = await axios.post<ApiResponse<Allocation>>(
        `${API_BASE_URL}allocations`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating allocation:", error);
      throw error;
    }
  },
  bulkCreateAllocationWithTags: async (
    data: CreateAllocationWithTagsRequest[]
  ): Promise<Allocation> => {
    try {
      const response = await axios.post<ApiResponse<Allocation>>(
        `${API_BASE_URL}allocations/bulk`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating allocation with tags:", error);
      throw error;
    }
  },

  // Create allocation with tags
  createAllocationWithTags: async (
    data: CreateAllocationWithTagsRequest
  ): Promise<Allocation> => {
    try {
      const response = await axios.post<ApiResponse<Allocation>>(
        `${API_BASE_URL}allocations/with-tags`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating allocation with tags:", error);
      throw error;
    }
  },

  // Get all allocations with optional filters
  getAllocations: async (filters?: AllocationFilter): Promise<Allocation[]> => {
    try {
      const queryParams = filters ? `?${buildQueryParams(filters)}` : "";
      const response = await axios.get<ApiResponse<Allocation[]>>(
        `${API_BASE_URL}allocations${queryParams}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching allocations:", error);
      throw error;
    }
  },

  // Get allocation by ID
  getAllocation: async (allocationId: string): Promise<Allocation> => {
    try {
      const response = await axios.get<ApiResponse<Allocation>>(
        `${API_BASE_URL}allocations/${allocationId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching allocation:", error);
      throw error;
    }
  },

  // Update allocation
  updateAllocation: async (
    allocationId: string,
    data: UpdateAllocationRequest
  ): Promise<Allocation> => {
    try {
      const response = await axios.put<ApiResponse<Allocation>>(
        `${API_BASE_URL}allocations/${allocationId}`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error updating allocation:", error);
      throw error;
    }
  },

  // Delete allocation
  deleteAllocation: async (allocationId: string): Promise<void> => {
    try {
      await axios.delete<ApiResponse<void>>(
        `${API_BASE_URL}allocations/${allocationId}`,
        { headers: await getAuthHeaders() }
      );
    } catch (error) {
      console.error("Error deleting allocation:", error);
      throw error;
    }
  },

  // Get allocations by project
  getAllocationsByProject: async (projectId: string): Promise<Allocation[]> => {
    try {
      const response = await axios.get<ApiResponse<Allocation[]>>(
        `${API_BASE_URL}allocations/project/${projectId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching allocations by project:", error);
      throw error;
    }
  },

  getAllocationSummaryByProject: async (
    projectId: string
  ): Promise<AllocationSummaryArray> => {
    try {
      const response = await axios.get<ApiResponse<AllocationSummaryArray>>(
        `${API_BASE_URL}allocations/summary/project/${projectId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching allocation summary by project:", error);
      throw error;
    }
  },

  // Get allocations by category
  getAllocationsByCategory: async (category: string): Promise<Allocation[]> => {
    try {
      const response = await axios.get<ApiResponse<Allocation[]>>(
        `${API_BASE_URL}allocations/category/${category}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching allocations by category:", error);
      throw error;
    }
  },

  getAllocationsByProjectAndCategory: async (
    projectId: string,
    category: string
  ): Promise<Allocation[]> => {
    try {
      const response = await axios.get<ApiResponse<Allocation[]>>(
        `${API_BASE_URL}allocations/project/${projectId}/${category}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error(
        "Error fetching allocations by project and category:",
        error
      );
      throw error;
    }
  },

  // Get allocations by date range
  getAllocationsByDateRange: async (
    startDate: string,
    endDate: string
  ): Promise<Allocation[]> => {
    try {
      const response = await axios.get<ApiResponse<Allocation[]>>(
        `${API_BASE_URL}allocations/date-range?startDate=${startDate}&endDate=${endDate}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching allocations by date range:", error);
      throw error;
    }
  },

  getAllocationsByProjectAndVehicle: async (
    projectId: string,
    vehicleId: string,
    lastReadingDate: string,
    currentReadingDate: string
  ): Promise<any> => {
    try {
      const queryParams = `?lastReadingDate=${lastReadingDate}&currentReadingDate=${currentReadingDate}`;
      const response = await axios.get<ApiResponse<any>>(
        `${API_BASE_URL}allocations/project/${projectId}/vehicle/${vehicleId}/resources${queryParams}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error(
        "Error fetching allocations by project and vehicle:",
        error
      );
      throw error;
    }
  },
};

// AllocationTag API Service
export const allocationTagApi = {
  // Create a new allocation tag
  createAllocationTag: async (
    data: CreateAllocationTagRequest
  ): Promise<AllocationTag> => {
    try {
      const response = await axios.post<ApiResponse<AllocationTag>>(
        `${API_BASE_URL}allocation-tags`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating allocation tag:", error);
      throw error;
    }
  },

  // Get all allocation tags with optional filters
  getAllocationTags: async (
    filters?: AllocationTagFilter
  ): Promise<AllocationTag[]> => {
    try {
      const queryParams = filters ? `?${buildQueryParams(filters)}` : "";
      const response = await axios.get<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags${queryParams}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching allocation tags:", error);
      throw error;
    }
  },

  // Get allocation tag by ID
  getAllocationTag: async (tagId: string): Promise<AllocationTag> => {
    try {
      const response = await axios.get<ApiResponse<AllocationTag>>(
        `${API_BASE_URL}allocation-tags/${tagId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching allocation tag:", error);
      throw error;
    }
  },

  // Update allocation tag
  updateAllocationTag: async (
    tagId: string,
    data: UpdateAllocationTagRequest
  ): Promise<AllocationTag> => {
    try {
      const response = await axios.put<ApiResponse<AllocationTag>>(
        `${API_BASE_URL}allocation-tags/${tagId}`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error updating allocation tag:", error);
      throw error;
    }
  },

  // Delete allocation tag
  deleteAllocationTag: async (tagId: string): Promise<void> => {
    try {
      await axios.delete<ApiResponse<void>>(
        `${API_BASE_URL}allocation-tags/${tagId}`,
        { headers: await getAuthHeaders() }
      );
    } catch (error) {
      console.error("Error deleting allocation tag:", error);
      throw error;
    }
  },

  // Get tags by category1
  getTagsByCategory1: async (
    category1Type: string,
    category1Id: string
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.get<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/category1/${category1Type}/${category1Id}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching tags by category1:", error);
      throw error;
    }
  },

  // Get tags by category2
  getTagsByCategory2: async (
    category2Type: string,
    category2Id: string
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.get<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/category2/${category2Type}/${category2Id}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching tags by category2:", error);
      throw error;
    }
  },

  // Get tags by category pair
  getTagsByCategoryPair: async (
    category1Type: string,
    category1Id: string,
    category2Type: string,
    category2Id: string
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.get<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/pair/${category1Type}/${category1Id}/${category2Type}/${category2Id}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching tags by category pair:", error);
      throw error;
    }
  },

  // Get tags by date range
  getTagsByDateRange: async (
    startDate: string,
    endDate: string
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.get<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/date-range?startDate=${startDate}&endDate=${endDate}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching tags by date range:", error);
      throw error;
    }
  },

  // Find tags for entity
  findTagsForEntity: async (
    entityType: string,
    entityId: string
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.get<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/entity/${entityType}/${entityId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error finding tags for entity:", error);
      throw error;
    }
  },

  // Find related entities
  findRelatedEntities: async (
    entityType: string,
    entityId: string
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.get<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/related/${entityType}/${entityId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error finding related entities:", error);
      throw error;
    }
  },

  // Bulk operations
  bulkCreateAllocationTags: async (
    tags: CreateAllocationTagRequest[]
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.post<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/bulk`,
        tags,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error bulk creating allocation tags:", error);
      throw error;
    }
  },

  bulkUpdateAllocationTags: async (
    tags: { id: string; data: UpdateAllocationTagRequest }[]
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.put<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/bulk`,
        tags,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error bulk updating allocation tags:", error);
      throw error;
    }
  },

  bulkDeleteAllocationTags: async (tagIds: string[]): Promise<void> => {
    try {
      await axios.delete<ApiResponse<void>>(
        `${API_BASE_URL}allocation-tags/bulk`,
        {
          headers: await getAuthHeaders(),
          data: { tagIds },
        }
      );
    } catch (error) {
      console.error("Error bulk deleting allocation tags:", error);
      throw error;
    }
  },

  // Create bidirectional tag
  createBidirectionalTag: async (
    data: CreateAllocationTagRequest
  ): Promise<AllocationTag[]> => {
    try {
      const response = await axios.post<ApiResponse<AllocationTag[]>>(
        `${API_BASE_URL}allocation-tags/bidirectional`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating bidirectional tag:", error);
      throw error;
    }
  },
};

// Legacy API Service for backward compatibility
export const projectModuleApi = {
  // Get all modules with optional filters
  getModules: async (filters?: ProjectFilter): Promise<ProjectModule[]> => {
    try {
      // Convert legacy filters to new format
      const projectFilters: ProjectFilter = {
        limit: filters?.limit,
        offset: filters?.offset,
        status: filters?.status as ProjectStatus[] | ProjectStatus.STARTED, // Take first status if array
        search: filters?.search,
        clientId: filters?.clientId,
      };

      const projects = await projectApi.getProjects(projectFilters);

      // Convert to legacy format
      return projects.map((project) => ({
        ...project,
        updatedBy: project.updatedBy || project.createdBy,
      }));
    } catch (error) {
      console.error("Error fetching modules:", error);
      return [];
    }
  },

  getAssignedModules: async (
    filters?: ProjectFilter
  ): Promise<ProjectModule[]> => {
    try {
      // Convert legacy filters to new format
      const projectFilters: ProjectFilter = {
        limit: filters?.limit,
        offset: filters?.offset,
        status: filters?.status as ProjectStatus[] | ProjectStatus.STARTED, // Take first status if array
        search: filters?.search,
        clientId: filters?.clientId,
      };

      const projects = await projectApi.getAssignedProjects(projectFilters);

      // Convert to legacy format
      return projects.map((project) => ({
        ...project,
        updatedBy: project.updatedBy || project.createdBy,
      }));
    } catch (error) {
      console.error("Error fetching modules:", error);
      return [];
    }
  },

  // Get a single module by ID
  getModule: async (id: string): Promise<ProjectModule> => {
    try {
      const project = await projectApi.getProject(id);
      return {
        ...project,
        updatedBy: project.updatedBy || project.createdBy,
      };
    } catch (error) {
      console.error("Error fetching module:", error);
      throw error;
    }
  },

  getSummary: async (id: string): Promise<Summary> => {
    try {
      const response = await axios.get<ApiResponse<Summary>>(
        `${API_BASE_URL}projects/summary/${id}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching summary:", error);
      throw error;
    }
  },

  // Create a new module
  createModule: async (data: CreateProjectRequest): Promise<ProjectModule> => {
    try {
      const projectData: CreateProjectRequest = {
        name: data.name,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
        clientId: data.clientId,
      };

      const project = await projectApi.createProject(projectData);
      return {
        ...project,
        updatedBy: project.createdBy,
      };
    } catch (error) {
      console.error("Error creating module:", error);
      throw error;
    }
  },

  // Update an existing module
  updateModule: async (data: UpdateProjectRequest): Promise<ProjectModule> => {
    try {
      const projectData: UpdateProjectRequest = {
        id: data.id,
        name: data.name,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
        clientId: data.clientId,
      };

      const project = await projectApi.updateProject(
        data?.id || "",
        projectData
      );
      return {
        ...project,
        updatedBy: project.updatedBy || project.createdBy,
      };
    } catch (error) {
      console.error("Error updating module:", error);
      throw error;
    }
  },

  // Delete a module
  deleteModule: async (id: string): Promise<void> => {
    try {
      await projectApi.deleteProject(id);
    } catch (error) {
      console.error("Error deleting module:", error);
      throw error;
    }
  },

  // Update module status
  updateModuleStatus: async (
    id: string,
    status: ProjectStatus
  ): Promise<void> => {
    try {
      await projectApi.updateProjectStatus(id, {
        status: status as ProjectStatus,
      });
    } catch (error) {
      console.error("Error updating module status:", error);
      throw error;
    }
  },

  getProjectActivityLogs: async (
    projectId: string,
    page: number = 1,
    limit: number = 10
  ): Promise<{
    data: ProjectActivityLog[];
    pagination: { total: number; page: number; pages: number };
  }> => {
    try {
      const queryParams = new URLSearchParams({
        page: page?.toString() || "1",
        limit: limit?.toString() || "10",
      });

      const response = await axios.get<
        ApiResponse<{
          data: ProjectActivityLog[];
          pagination: { total: number; page: number; pages: number };
        }>
      >(`${API_BASE_URL}recent-activities/${projectId}?${queryParams}`, {
        headers: await getAuthHeaders(),
      });
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching project activity logs:", error);
      return { data: [], pagination: { total: 0, page: 1, pages: 1 } };
    }
  },
};

// Master Data Allocation API Service
export const masterDataAllocationApi = {
  // Create master data and allocate to project
  createMasterDataWithAllocation: async (data: {
    projectId: string;
    masterDataType: string;
    masterData: any;
  }) => {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}master-data/create`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating master data with allocation:", error);
      throw error;
    }
  },

  // Allocate existing master data to project
  allocateExistingMasterData: async (data: {
    projectId: string;
    masterDataId: string;
    masterDataType: string;
  }) => {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}master-data/allocate`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error allocating existing master data:", error);
      throw error;
    }
  },

  // Get master data allocations by project
  getMasterDataAllocationsByProject: async (
    projectId: string,
    masterDataType?: string
  ) => {
    try {
      const queryParams = masterDataType
        ? `?masterDataType=${masterDataType}`
        : "";
      const response = await axios.get<ApiResponse<any[]>>(
        `${API_BASE_URL}master-data/project/${projectId}${queryParams}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error(
        "Error fetching master data allocations by project:",
        error
      );
      throw error;
    }
  },

  // Get master data allocations by type
  getMasterDataAllocationsByType: async (
    masterDataType: string,
    projectId?: string
  ) => {
    try {
      const queryParams = projectId ? `?projectId=${projectId}` : "";
      const response = await axios.get<ApiResponse<any[]>>(
        `${API_BASE_URL}master-data/type/${masterDataType}${queryParams}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching master data allocations by type:", error);
      throw error;
    }
  },

  // Remove master data allocation
  removeMasterDataAllocation: async (allocationId: string) => {
    try {
      const response = await axios.delete<ApiResponse<any>>(
        `${API_BASE_URL}master-data/${allocationId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error removing master data allocation:", error);
      throw error;
    }
  },

  // Bulk allocate master data
  bulkAllocateMasterData: async (data: {
    projectId: string;
    allocations: Array<{
      masterDataId: string;
      masterDataType: string;
    }>;
  }) => {
    try {
      const response = await axios.post<ApiResponse<any[]>>(
        `${API_BASE_URL}master-data/bulk-allocate`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error bulk allocating master data:", error);
      throw error;
    }
  },
};

export const documentApi = {
  // delete a document
  deleteDocument: async (documentId: string) => {
    try {
      const response = await axios.delete<ApiResponse<any>>(
        `${process.env.REACT_APP_API_HOST}documents/${documentId}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error deleting document:", error);
      throw error;
    }
  },
};

export const superProjectApi = {
  // get all super projects
  getSuperProjects: async (
    filters?: ProjectFilter
  ): Promise<SuperProject[]> => {
    try {
      // Convert legacy filters to new format
      const projectFilters: ProjectFilter = {
        limit: filters?.limit,
        offset: filters?.offset,
        status: filters?.status as ProjectStatus[] | ProjectStatus.STARTED, // Take first status if array
        search: filters?.search,
        clientId: filters?.clientId,
      };
      const queryParams = new URLSearchParams({
        limit: projectFilters?.limit?.toString() || "10",
        offset: projectFilters?.offset?.toString() || "0",
        status: projectFilters?.status?.toString() || "",
        search: projectFilters?.search || "",
        clientId: projectFilters?.clientId || "",
      });
      const response = await axios.get<ApiResponse<SuperProject[]>>(
        `${API_BASE_URL}super-projects?${queryParams} `,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching super projects:", error);
      throw error;
    }
  },
  // get super project by id
  getSuperProject: async (
    id: string,
    queryParams?: string
  ): Promise<SuperProject> => {
    try {
      const response = await axios.get<ApiResponse<SuperProject>>(
        `${API_BASE_URL}super-projects/${id}?${queryParams}`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching super project:", error);
      throw error;
    }
  },
  // get super project summary
  getSuperProjectSummary: async (id: string): Promise<Summary> => {
    try {
      const response = await axios.get<ApiResponse<Summary>>(
        `${API_BASE_URL}super-projects/${id}/summary`,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching super project summary:", error);
      throw error;
    }
  },
  // get divisions by super project id
  getDivisionsBySuperProjectId: async (
    id: string
  ): Promise<SuperProjectWithAllocationSummary[]> => {
    try {
      const response = await axios.get<
        ApiResponse<SuperProjectWithAllocationSummary[]>
      >(`${API_BASE_URL}super-projects/${id}/divisions`, {
        headers: await getAuthHeaders(),
      });
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error fetching divisions by super project id:", error);
      throw error;
    }
  },
  // create super project
  createSuperProject: async (data: any): Promise<any> => {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}super-projects`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating super project:", error);
      throw error;
    }
  },
  // update super project
  updateSuperProject: async (id: string, data: any): Promise<any> => {
    try {
      const response = await axios.put<ApiResponse<any>>(
        `${API_BASE_URL}super-projects/${id}`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error updating super project:", error);
      throw error;
    }
  },
  // create division
  createDivision: async (
    superProjectId: string,
    data: any
  ): Promise<SuperProject> => {
    try {
      const response = await axios.post<ApiResponse<SuperProject>>(
        `${API_BASE_URL}super-projects/${superProjectId}/divisions`,
        data,
        { headers: await getAuthHeaders() }
      );
      return handleApiResponse(response);
    } catch (error) {
      console.error("Error creating division:", error);
      throw error;
    }
  },
};
